import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { OverlayEventDetail } from '@ionic/core';

import { ToastParams } from '../../models/toast/toast-params.model';

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  constructor(private readonly toastCtrl: ToastController) {}

  /**
   * Displays a toast notification with the specified parameters.
   *
   * Default params:
   * ```typescript
   * {
   *   duration: 3000
   *   position: 'top',
   *   color: 'success',
   * }
   * ```
   *
   * @param {ToastParams} params - The parameters for the toast notification.
   * @param {string} [params.header] - The header to display in the toast.
   * @param {string} params.message - The message to display in the toast.
   * @param {number} [params.duration=3000] - The duration for which the toast should be visible (in milliseconds).
   * @param {string} [params.position='top'] - The position of the toast on the screen ('top', 'bottom', or 'middle').
   * @param {string} [params.color='success'] - The color of the toast.
   * @param {Array<Object>} [params.buttons] - The buttons to include in the toast.
   * @returns {Promise<OverlayEventDetail<any>>} A promise that resolves when the toast is dismissed.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  showToast = async (params: ToastParams): Promise<OverlayEventDetail<any>> => {
    const toast = await this.toastCtrl.create({
      header: params.header ?? '',
      message: params.message,
      duration: params.duration ?? 3000,
      swipeGesture: 'vertical',
      position: params.position ?? 'top',
      color: params.color ?? 'success',
      buttons: params.buttons,
    });

    await toast.present();
    return await toast.onDidDismiss();
  };
}
